import { useLocalization } from "@progress/kendo-react-intl";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { FrequencyTypes } from "../../../../Constants/SchedulerConstants";
import SelectedWeeks from "../../../../Models/Requirement/SelectedWeeks";
import { FieldWrapper } from '@progress/kendo-react-form';
import { ComboBox, ComboBoxFilterChangeEvent, DropDownList } from "@progress/kendo-react-dropdowns";
import { getValidStartDateForFrequency, rRuleGenerator, rruleFrequencyToTextRRule } from "../../Utilities/RequirementUtilities";
import { enMessages } from "../../../../messages/en-US";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import DayPickerMonth2 from "../../../Shared/Components/Forms/DayPickerMonth2";
import { Calendar, CalendarProps, DatePicker } from "@progress/kendo-react-dateinputs";
import { timezoneToComboboxItem, timezonesToComboboxItems } from "../../../Area/Utilities/AreaUtilities";
import { useAccountSettingsStore } from "../../../../Zustand/accountSettingsStore";
import { Weekday, rrulestr } from "rrule";
import DayPickerWeek2 from "../../../Shared/Components/Forms/DayPickerWeek2";
import { Error } from "@progress/kendo-react-labels";
import { FilterDescriptor, filterBy } from "@progress/kendo-data-query";

export default function RecurrenceRuleInput(props)
{
    const { validationMessage, visited} = props;
    const [interval, setInterval] = React.useState<number | undefined>(1);
    const [windowComplianceTime, setWindowComplianceTime] = React.useState<number | undefined>(props.value !== undefined && props.value.windowComplianceTime !== undefined ? props.value.windowComplianceTime : 720);
    const accountTimezone = useAccountSettingsStore((state) => state.accountTimezone);
    const defaultTimezone = props.value !== undefined && props.value.timezone !== "" ? props.value.timezone : (accountTimezone || moment.tz.guess());
    const [timezone, setTimezone] = useState<string>(defaultTimezone);
    const [startDate, setStartDate] = React.useState<Date | null>(props.value !== undefined && props.value.startDate != null ? new Date(new Date(props.value?.startDate!).setHours(0,0,0,0)) : new Date(new Date().setHours(0,0,0,0)));
    const [endDate, setEndDate] = React.useState<Date | null>(props.value !== undefined && props.value.endDate != null ? new Date(new Date(props.value?.endDate!).setHours(23,59,59,59)) : null);

    const [selectedWeeks, setSelectedWeeks] = useState<SelectedWeeks>(
    {week1Selected:[], week2Selected: [], week3Selected: [], week4Selected: []});

    const localizationService = useLocalization();
    const [frequency, setFrequency] = useState<string>(FrequencyTypes.daily);
    const [showSelectionErrorMessage] = useState<boolean>(false);
    const [daysSelected, setDaysSelected] = useState<Array<number>>([]);
    const [timezoneData, setTimezoneData] = React.useState(timezonesToComboboxItems().slice());

    // Disable Fast Navigational Bar until this critical bug is fixed by Telerik (https://github.com/telerik/kendo-react/issues/1566)
    const CustomCalendar = (props: CalendarProps) => {
      return <Calendar {...props} navigation={false} />
    };

    useEffect(() => {
        if(props.value !== undefined) {
            // If rRule is empty string, it's Per Pump Cycle
            if (!props.value.rRule || props.value.rRule.trim() === '') {
                setFrequency(FrequencyTypes.pumpCycle);
                return;
            }

            try {
                const rRuleAsString = props.value.rRule.split('RRULE:')[1];
                
                // Check if rRuleAsString is empty (pump cycle case)
                if (!rRuleAsString || rRuleAsString.trim() === '') {
                    setFrequency(FrequencyTypes.pumpCycle);
                    return;
                }
                
                const rRule = rrulestr(rRuleAsString);
                const newFrequency = rruleFrequencyToTextRRule(rRuleAsString);
                
                // If rruleFrequencyToTextRRule returns empty string, it's pump cycle
                if (!newFrequency || newFrequency.trim() === '') {
                    setFrequency(FrequencyTypes.pumpCycle);
                    return;
                }
                
                setFrequency(newFrequency);
                
                // Set interval to 360 if it's secondly and no interval was specified
                if (newFrequency === FrequencyTypes.secondly && !rRule.origOptions.interval) {
                    setInterval(360);
                } else {
                    setInterval(rRule.origOptions.interval);
                }

                if (rRule.origOptions.byweekday !== undefined || rRule.origOptions.byweekday != null) {
                    var weekDays = rRule.origOptions.byweekday as Array<Weekday>;

                    setDaysSelected(weekDays.filter(weekDay => weekDay.n === undefined).map(weekDays => weekDays.getJsWeekday()));
                    var week1SelectedDays = weekDays.filter(weekDay => weekDay.n === 1).map(weekDay => weekDay.getJsWeekday());
                    var week2SelectedDays = weekDays.filter(weekDay => weekDay.n === 2).map(weekDay => weekDay.getJsWeekday());
                    var week3SelectedDays = weekDays.filter(weekDay => weekDay.n === 3).map(weekDay => weekDay.getJsWeekday());
                    var week4SelectedDays = weekDays.filter(weekDay => weekDay.n === 4).map(weekDay => weekDay.getJsWeekday());   

                    setSelectedWeeks({
                        week1Selected: week1SelectedDays, 
                        week2Selected: week2SelectedDays, 
                        week3Selected: week3SelectedDays, 
                        week4Selected: week4SelectedDays
                    });
                }
            } catch (error) {
                console.warn('Error parsing rRule:', error);
                setFrequency(FrequencyTypes.daily);
            }
        }
    },[]);

    useEffect(() => {
        if (frequency === FrequencyTypes.secondly && (!interval || interval < 360)) {
            setInterval(360);
        } else if (frequency === FrequencyTypes.pumpCycle && (!interval || interval < 360)) {
            setInterval(360);
        } else if (frequency !== FrequencyTypes.secondly && frequency !== FrequencyTypes.pumpCycle && interval === 360) {
            setInterval(1);
        }
    }, [frequency]);

    useEffect(() => {
        let recurrence = 1;
        if(interval !== undefined) {
            recurrence = interval;
        }

        const rRule = rRuleGenerator(
            recurrence,
            startDate,
            endDate,
            frequency,
            daysSelected,
            selectedWeeks.week1Selected,
            selectedWeeks.week2Selected,
            selectedWeeks.week3Selected,
            selectedWeeks.week4Selected
        );

        const validDate = getValidStartDateForFrequency(startDate, frequency);
        const recurrenceRule = {
            rRule: rRule ? rRule.toString() : "", // Handle null rRule for pump cycle
            startDate: validDate, 
            endDate: endDate, 
            timezone: timezone,
            windowComplianceTime: windowComplianceTime
        };
        props.onChange({ value: recurrenceRule});

    },[frequency, interval, daysSelected, selectedWeeks, startDate, endDate, timezone, windowComplianceTime]);


const onRecurrenceChange = (event) => {
  setInterval(event.value);
}

const onWindowComplianceTimeChange = (event) => {
  setWindowComplianceTime(event.value);
}

  const FrequencyInput = (fieldRenderProps) => {
    const frequencyOptions = [
        {
            displayName: `${localizationService.toLanguageString('custom.daily', enMessages.custom.daily)}`,
            value: FrequencyTypes.daily,
        },
        {
            displayName: `${localizationService.toLanguageString('custom.weekly', enMessages.custom.weekly)}`,
            value: FrequencyTypes.weekly,
        },
        {
            displayName: `${localizationService.toLanguageString('custom.fixedIntervals', enMessages.custom.fixedIntervals)}`,
            value: FrequencyTypes.secondly,
        },
        {
            displayName: `${localizationService.toLanguageString('custom.pumpCycle', enMessages.custom.pumpCycle)}`,
            value: FrequencyTypes.pumpCycle,
        },
    ];

    const handleFrequencyChange = (event: any) => {
        const newFrequency = event.value.value || event.value;
        setFrequency(newFrequency);
        // Interval will be updated by the useEffect above
    };

    return (
        <div className="py-2">
            <label htmlFor="frequency">
                {localizationService.toLanguageString('custom.frequency', enMessages.custom.frequency)}
            </label>
       
            <DropDownList
                style={{ height: "30px" }}
                data={frequencyOptions}
                textField="displayName"
                dataItemKey="value"
                value={frequencyOptions.find(option => option.value === frequency)}
                onChange={handleFrequencyChange}
            />
        </div>
    );
};

const TimezoneInput = () => {
    const filterData = (filter: FilterDescriptor) => {
        const data = timezonesToComboboxItems().slice();
        return filterBy(data, filter);
    };

    const filterChange = (event: ComboBoxFilterChangeEvent) => {
        setTimezoneData(filterData(event.filter));
    };

    return (
            <div className="py-2 w-full">
            <label htmlFor="timezone">{localizationService.toLanguageString('custom.timezone', enMessages.custom.timezone)}</label>
        
            <ComboBox
                data={timezoneData}
                textField="displayName"
                name="timezone"
                className="form-field"
                placeholder={localizationService.toLanguageString('custom.timezone', enMessages.custom.timezone)}
                defaultValue={timezoneToComboboxItem(timezone)}
                onChange={(event) => {
                  event.value === null ? setTimezone('') :
                  setTimezone(event.value.value)}}
                filterable={true}
                onFilterChange={filterChange}
                required={true}
            />

            
            {visited && validationMessage && validationMessage === 'Timezone is required' &&<Error>{validationMessage}</Error>}
        </div>
    );
};

const StartDateInput = () => {

    return (
        <div className="py-2 w-full">
        <label htmlFor="startDate">{localizationService.toLanguageString('custom.startDate', enMessages.custom.startDate)}</label>
    
        <DatePicker
            onChange={(event) => {setStartDate(event.target.value)}}
            defaultValue={startDate}
            value={startDate}
            calendar={CustomCalendar}
        />
      </div>
    );
  };

  const EndDateInput = () => {
    return (
        <div className="py-2 w-full">
        <label htmlFor="endDate">{localizationService.toLanguageString('custom.endDate', enMessages.custom.endDate)}</label>
      
      <DatePicker
        onChange={(event) =>{setEndDate(event.target.value)}}
        defaultValue={props.value !== undefined && props.value.endDate != null ? new Date(props.value?.endDate!) : null}
        placeholder={localizationService.toLanguageString('custom.endDate', enMessages.custom.endDate)}
        min={startDate !== null ? startDate : new Date()}
        value={endDate}
        calendar={CustomCalendar}
      />
      </div>
    );
  };

  const MonthlyRecurrenceInput = () => {
    return (
    <div className="py-2">
    <label htmlFor="weeklyRecurrence">{localizationService.toLanguageString('custom.monthlyRecurrence', enMessages.custom.monthlyRecurrence)}</label>
    <DayPickerMonth2 selectedWeeks={selectedWeeks} onHandleChange={setSelectedWeeks} />

    {showSelectionErrorMessage && (
        <div className="text-xs text-red-600">* {localizationService.toLanguageString('custom.mustSelectMonth', enMessages.custom.mustSelectMonth)}</div>
    )}
    <label htmlFor="recurrenceInterval">{localizationService.toLanguageString('custom.pumpCycleInterval', enMessages.custom.pumpCycleInterval)}</label>
    <NumericTextBox
       onChange={(onRecurrenceChange)}
       defaultValue={interval}
       value={interval}
       min={1}
       name="recurrence"
       className="k-input k-input-md k-rounded-md k-input-solid h-8"
        placeholder={localizationService.toLanguageString('custom.recurranceXMonths', enMessages.custom.recurranceXMonths)} />
    </div>
    );
    }

    const WeeklyRecurrenceInput = () => {
        return (
            <div className="py-2">
            <label htmlFor="weeklyRecurrence">{localizationService.toLanguageString('custom.weeklyRecurrenceInterval', enMessages.custom.weeklyRecurrenceInterval)}</label>
            <DayPickerWeek2 daysSelected={daysSelected} setDaysSelected={setDaysSelected}/>
            
            {showSelectionErrorMessage && (
              <div className="text-xs text-red-600">* {localizationService.toLanguageString('custom.mustSelectWeek', enMessages.custom.mustSelectWeek)}</div>
            )}
            <label htmlFor="recurrenceInterval">{localizationService.toLanguageString('custom.recurrenceInterval', enMessages.custom.recurrenceInterval)}</label>
             <NumericTextBox
             onChange={(onRecurrenceChange)}
             defaultValue={interval}
             value={interval}
             min={1}
             name="recurrence"
             className="k-input k-input-md k-rounded-md k-input-solid h-8"
              placeholder={localizationService.toLanguageString('custom.recurranceXWeeks', enMessages.custom.recurranceXWeeks)}
            />
          </div>
        );
        }

        const DailyRecurrenceInput = () => {
            return (
                <div className="py-2">
            <label htmlFor="dailyRecurrence">{localizationService.toLanguageString('custom.dailyRecurrenceInterval', enMessages.custom.dailyRecurrenceInterval)}</label>
            <NumericTextBox
                onChange={(onRecurrenceChange)}
                defaultValue={interval}
                value={interval}
                min={1}
                name="recurrence"
                className="k-input k-input-md k-rounded-md k-input-solid h-8"
                placeholder={localizationService.toLanguageString('custom.recurranceXDays', enMessages.custom.recurranceXDays)}
            />
            </div>       
            );
            }

  const FixedIntervalsRecurrenceInput = () => {
    return (
      <div className="py-2">
        <label htmlFor="fixedIntervalsRecurrence">{localizationService.toLanguageString('custom.fixedIntervalsInterval', enMessages.custom.fixedIntervalsInterval)}</label>
        <NumericTextBox
          onChange={(onRecurrenceChange)}
          defaultValue={interval}
          value={interval}
          min={30}
          max={3600}
          name="recurrence"
          className="k-input k-input-md k-rounded-md k-input-solid h-8"
          // placeholder={localizationService.toLanguageString('custom.recurranceXMinutes', enMessages.custom.recurranceXMinutes)}
        />
      </div>
    );
  }

  const SecondlyRecurrenceInput = () => {
    return (
      <div className="py-2">
        <label htmlFor="secondlyRecurrence">{localizationService.toLanguageString('custom.pumpCycleInterval', enMessages.custom.pumpCycleInterval)}</label>
        <NumericTextBox
          onChange={(onRecurrenceChange)}
          defaultValue={interval}
          value={interval}
          min={30}
          max={3600}
          name="recurrence"
          className="k-input k-input-md k-rounded-md k-input-solid h-8"
          // placeholder={localizationService.toLanguageString('custom.recurranceXMinutes', enMessages.custom.recurranceXMinutes)}
        />
      </div>
    );
  }

  const WindowComplianceTimeInput = () => {
    return (
      <div className="py-2">
        <label htmlFor="windowComplianceTime">{localizationService.toLanguageString('custom.windowComplianceTime', 'Window Compliance Time (minutes)')}</label>
        <NumericTextBox
          onChange={(onWindowComplianceTimeChange)}
          defaultValue={windowComplianceTime}
          value={windowComplianceTime}
          min={0}
          name="windowComplianceTime"
          className="k-input k-input-md k-rounded-md k-input-solid h-8"
          placeholder="Enter window compliance time"
        />
      </div>
    );
  }

  return (
    <FieldWrapper>
    
        <FrequencyInput />

        {frequency === FrequencyTypes.daily && (
            DailyRecurrenceInput()
        )}

        {frequency === FrequencyTypes.weekly && (
             WeeklyRecurrenceInput()
        )}

        {frequency === FrequencyTypes.monthly && (
            MonthlyRecurrenceInput()
        )}

        {frequency === FrequencyTypes.secondly && (
            FixedIntervalsRecurrenceInput()
        )}

        {frequency === FrequencyTypes.secondly && (
            WindowComplianceTimeInput()
        )}

        {frequency === FrequencyTypes.pumpCycle && (
            WindowComplianceTimeInput()
        )}

        {/* TODO: Add window compliance time input */}
        {/* {WindowComplianceTimeInput()} */}
        {TimezoneInput()}
        {StartDateInput()}
        {EndDateInput()}

        {visited && validationMessage && validationMessage === 'End Date must be greater than Start Date' && <Error>{validationMessage}</Error>}
       
    </FieldWrapper>
  );
}